// components/PieChartComponent.js
import React, { useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

// Short money (e.g., $2.5B)
const toShortMoney = (v) => {
  if (v == null || isNaN(v)) return '';
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000)     return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)         return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v}`;
};

function collapseToTopN(data, topN = 5) {
  if (!Array.isArray(data)) return [];
  const clean = data
    .filter(d => d && typeof d.value === 'number' && isFinite(d.value))
    .sort((a, b) => b.value - a.value);

  if (clean.length <= topN) return clean;
  const head = clean.slice(0, topN);
  const tailSum = clean.slice(topN).reduce((s, d) => s + d.value, 0);
  return [...head, { name: 'Other', value: tailSum }];
}

// Brand pie palette (dark → light)
const BLUES = ['#0B1E39', '#14396D', '#1E4FA3', '#2563EB', '#60A5FA', '#93C5FD'];

/**
 * Props:
 *  - data: Array<{ name: string, value: number }>
 *  - height?: number (optional)
 */
export default function PieChartComponent({ data = [], height }) {
  let isMobile = false;
  try { isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches; } catch {}
  const h = height ?? (isMobile ? 220 : 260);

  const pieData = useMemo(() => collapseToTopN(data, 5), [data]);
  const total = useMemo(() => pieData.reduce((s, d) => s + (Number(d.value) || 0), 0), [pieData]);

  return (
    <div className="mix-chart" style={{ width: '100%', height: h }}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            innerRadius={isMobile ? 48 : 58}
            outerRadius={isMobile ? 80 : 92}
            paddingAngle={1}
            stroke="#fff"
            strokeWidth={1}
            isAnimationActive={false}
          >
            {pieData.map((entry, i) => (
              <Cell key={`cell-${i}`} fill={BLUES[i % BLUES.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, _name, ctx) => {
              const v = Number(value) || 0;
              const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0.0';
              return [`${toShortMoney(v)} (${pct}%)`, ctx?.payload?.name || ''];
            }}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
